import React, { Dispatch, ReactNode, useReducer } from 'react';

interface User {
  id: string | number;
  role: string;
  [key: string]: unknown;
}

type SessionState = User | null;

type SessionAction =
  | { type: 'login'; payload: User }
  | { type: 'logout' };

const SessionStateContext = React.createContext<SessionState | undefined>(undefined);
const SessionDispatchContext = React.createContext<Dispatch<SessionAction> | undefined>(undefined);

function authReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'login': {
      return { ...action.payload };
    }
    case 'logout': {
      return null;
    }
    default: {
      throw new Error(`Unhandled action type: ${(action as SessionAction).type}`);
    }
  }
}

interface SessionProviderProps {
  children: ReactNode;
  init?: SessionState;
}

function SessionProvider({ children, init = null }: SessionProviderProps) {
  const [state, dispatch] = useReducer(authReducer, init);

  return (
    <SessionStateContext.Provider value={state}>
      <SessionDispatchContext.Provider value={dispatch}>
        {children}
      </SessionDispatchContext.Provider>
    </SessionStateContext.Provider>
  );
}

function useSessionState(): SessionState {
  const context = React.useContext(SessionStateContext);
  if (context === undefined) {
    throw new Error('useSessionState must be used within a SessionProvider');
  }
  return context;
}

function useSessionDispatch(): Dispatch<SessionAction> {
  const context = React.useContext(SessionDispatchContext);
  if (context === undefined) {
    throw new Error('useSessionDispatch must be used within a SessionProvider');
  }
  return context;
}

export { SessionProvider, useSessionState, useSessionDispatch };
export type { SessionState, SessionAction, User };
